import logger from '../Helpers/logger';
import dbConnectionManager from './DbConnectionManager';

const RECENT_CHATS = 'recent_chats';

class RecentChatIdb {
    constructor() {
        if (!RecentChatIdb.instance) {
            RecentChatIdb.instance = this;
        }
        return RecentChatIdb.instance;
    }

    async getConnection() {
        return dbConnectionManager.getConnection();
    }

    async getRecentChats() {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([RECENT_CHATS], 'readonly');
                const objectStore = transaction.objectStore(RECENT_CHATS);
                const index = objectStore.index('timestamp');
                const results = [];
                const request = index.openCursor(null, 'prev');

                request.onsuccess = event => {
                    const cursor = event.target.result;
                    if (cursor) {
                        results.push(cursor.value);
                        cursor.continue();
                    } else {
                        logger.debug(`Fetched ${results.length} chats from DB sorted by timestamp+messageTime.`);
                        resolve(results);
                    }
                };

                request.onerror = event => {
                    logger.error('getRecentChats error:', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('error getRecentChats catch:', error);
            return [];
        }
    }

    async setRecentChats(chats) {
        try {
            logger.debug(`Attempting to save ${chats.length} chats to DB`);
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([RECENT_CHATS], 'readwrite');
                const objectStore = transaction.objectStore(RECENT_CHATS);

                chats.forEach(chat => {
                    const key = chat.fromUserId || chat.groupId || chat.userId;
                    if (key) {
                        // Ensure required indexing fields are present
                        if (!chat.timestamp) {
                            chat.timestamp = Date.now();
                        }
                        if (!chat.messageTime) {
                            // Fallback for messageTime: timestamp * 1000
                            chat.messageTime = chat.timestamp * 1000;
                        }
                        objectStore.put(chat, key);
                    } else {
                        logger.warn('Skipping chat with no key:', chat);
                    }
                });

                transaction.oncomplete = () => {
                    logger.debug('setRecentChats transaction complete');
                    resolve();
                };

                transaction.onerror = event => {
                    logger.error('setRecentChats transaction error:', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('error setRecentChats', error);
        }
    }

    async updateRecentChat(chatId, chat) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([RECENT_CHATS], 'readwrite');
                const objectStore = transaction.objectStore(RECENT_CHATS);

                // Ensure required indexing fields are present
                if (!chat.timestamp) {
                    chat.timestamp = Date.now();
                }
                if (!chat.messageTime) {
                    // Fallback for messageTime: timestamp * 1000
                    chat.messageTime = chat.timestamp * 1000;
                }

                const request = objectStore.put(chat, chatId);

                request.onsuccess = () => {
                    resolve(request.result);
                };

                request.onerror = event => {
                    logger.error('updateRecentChat error:', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('error updateRecentChat', error);
        }
    }

    async deleteRecentChat(chatId) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([RECENT_CHATS], 'readwrite');
                const objectStore = transaction.objectStore(RECENT_CHATS);
                const request = objectStore.delete(chatId);

                request.onsuccess = () => {
                    resolve(request.result);
                };

                request.onerror = event => {
                    logger.error('deleteRecentChat error:', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('error deleteRecentChat', error);
        }
    }

    async getRecentChatById(chatId) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([RECENT_CHATS], 'readonly');
                const objectStore = transaction.objectStore(RECENT_CHATS);
                const request = objectStore.get(chatId);

                request.onsuccess = event => {
                    resolve(event.target.result);
                };

                request.onerror = event => {
                    logger.error('getRecentChatById error:', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('error getRecentChatById catch:', error);
            return null;
        }
    }
}

const recentChatIdb = new RecentChatIdb();

if (typeof window !== 'undefined') {
    window.inspectRecentChats = async () => {
        const chats = await recentChatIdb.getRecentChats();
        logger.debug(`[RecentChatIdb] Found ${chats.length} recent chats`);
        if (chats.length > 0) {
            logger.debug(chats);
        }
        return chats;
    };

    window.inspectRecentChatById = async chatId => {
        const chat = await recentChatIdb.getRecentChatById(chatId);
        if (chat) {
            logger.debug(`[RecentChatIdb] Found chat for ${chatId}:`, chat);
        } else {
            logger.debug(`[RecentChatIdb] No chat found for ${chatId}`);
        }
        return chat;
    };
}

export default recentChatIdb;
